import type { Item } from "@wayflyer/flyui";
import { AutocompleteField, FieldSet, FormGroup } from "@wayflyer/flyui";
import { RhfAutocompleteField, RhfTextField } from "@wayflyer/flyui-hook-form";
import { m } from "framer-motion";
import { useIntl } from "react-intl";

import type { KycForm, KycFormFields } from "../../contexts/kycFormContext";
import type { Configuration, SupportedCountry } from "../interface";

export interface CompanyInfoAddressFormProps {
  kycConfiguration: Configuration;
  animate?: any;
  label?: string;
  tooltip?: string;
  initialCompanyAddressState?: string;
  fields: Record<string, keyof KycFormFields>;
  kycForm: KycForm;
  wrapInFieldSet?: boolean;
}

const supportedCountries: Record<SupportedCountry, string> = {
  US: "United States",
  GB: "United Kingdom",
  IE: "Ireland",
  AU: "Australia",
  CA: "Canada",
  ES: "Spain",
  NL: "Netherlands",
  BE: "Belgium",
  DK: "Denmark",
  SE: "Sweden",
  NZ: "New Zealand",
  DE: "Germany",
  FR: "France",
};
// We could generate this array directly, without building `supportedCountries` first,
// but the benefit of having `supportedCountries` is that we can ask TypeScript to check the exhaustiveness
// of the keys - which is quite useful to prevent ourselves from forgetting the addition of new countries there.
// (yes, this did happen a couple of times already ^^)
export const supportedCountriesItems: Item<SupportedCountry>[] = Object.entries(supportedCountries).map(
  ([id, text]) => {
    return { id: id as SupportedCountry, text };
  }
);

export const CompanyInfoAddressForm = ({
  kycConfiguration,
  initialCompanyAddressState,
  animate,
  fields,
  tooltip,
  kycForm,
  label,
  wrapInFieldSet = true,
}: CompanyInfoAddressFormProps) => {
  const intl = useIntl();

  const {
    stateRegion: { label: stateLabel, options: stateOptions },
    country,
    postalCodeLabel,
  } = kycConfiguration;

  const WrapperComponent = wrapInFieldSet ? FieldSet : FormGroup;

  return (
    <m.div initial={animate} animate={animate} transition={{ duration: 0.3 }}>
      <WrapperComponent legend={label ?? ""}>
        <RhfTextField<KycFormFields>
          form={kycForm}
          name={fields.address1}
          type="text"
          label={intl.formatMessage({
            id: "Address line 1",
            defaultMessage: "Address line 1",
          })}
          tooltip={tooltip}
        />
        <RhfTextField<KycFormFields>
          form={kycForm}
          name={fields.address2}
          type="text"
          label={intl.formatMessage({
            id: "Address line 2 (optional)",
            defaultMessage: "Address line 2 (optional)",
          })}
        />
        <RhfTextField<KycFormFields>
          form={kycForm}
          name={fields.city}
          label={intl.formatMessage({ id: "City", defaultMessage: "City" })}
        />
        {stateOptions ? (
          <RhfAutocompleteField<KycFormFields>
            form={kycForm}
            name={fields.state}
            options={stateOptions}
            label={stateLabel}
            initialValue={initialCompanyAddressState}
          />
        ) : (
          <RhfTextField<KycFormFields> form={kycForm} name={fields.state} label={stateLabel} />
        )}

        <RhfTextField<KycFormFields> form={kycForm} name={fields.zip} label={postalCodeLabel} />
        <AutocompleteField
          name="company-info-country"
          initialValue={country}
          label={intl.formatMessage({ id: "Country", defaultMessage: "Country" })}
          options={supportedCountriesItems}
          onChange={() => {}}
          readonly
        />
      </WrapperComponent>
    </m.div>
  );
};
